import { useEffect, useState } from 'react';
import { api, ErrorApi } from '../api/cliente.js';
import { useToast } from '../componentes/Toast.js';

interface Solicitud {
  id: string;
  fechaInicio: string;
  domicilio: string;
  tipoControl: string;
  tipoControlNombre: string;
  estado: string;
  creadaEn: string;
  empresaNombre: string;
  trabajador: {
    nombre: string;
    dni: string;
    legajo: string | null;
  };
}

interface Medico {
  id: string;
  nombre: string;
  email: string;
  refepsValidada: number;
  firmaEnrolado: number;
  activo: number;
}

export function Solicitudes() {
  const [solicitudes, setSolicitudes] = useState<Solicitud[] | null>(null);
  const [medicos, setMedicos] = useState<Medico[] | null>(null);
  const [error, setError] = useState('');
  const toast = useToast();

  async function cargarDatos() {
    try {
      const [solRes, medRes] = await Promise.all([
        api.get<Solicitud[]>('/api/operador/solicitudes'),
        api.get<Medico[]>('/api/operador/medicos'),
      ]);
      setSolicitudes(solRes);
      setMedicos(medRes);
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudieron cargar las solicitudes de control');
    }
  }

  useEffect(() => {
    void cargarDatos();
  }, []);

  if (error) {
    return <div className="error-box">{error}</div>;
  }

  return (
    <section style={{ animation: 'fadeInUp 0.4s ease forwards' }}>
      <h2>📥 Solicitudes de Control Médico</h2>
      <p className="muted">
        Listado de ausencias activas para las que la empresa ha solicitado un control. Asigná un médico para que realice la auditoría.
      </p>

      {solicitudes === null || medicos === null ? (
        <div className="cargando">Cargando solicitudes…</div>
      ) : solicitudes.length === 0 ? (
        <p className="muted">No hay solicitudes de control pendientes de asignación en este momento.</p>
      ) : (
        <>
          {medicos.filter((m) => m.refepsValidada === 1 && m.firmaEnrolado === 1 && m.activo === 1).length === 0 && (
            <div className="error-box" style={{ marginBottom: 16 }}>
              No hay médicos habilitados. Habilitá al menos un médico desde el panel de Médicos antes de asignar controles.
            </div>
          )}
          <div className="tabla-contenedor">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Trabajador</th>
                  <th>Empresa</th>
                  <th>Fecha de Ausencia</th>
                  <th>Tipo de Control</th>
                  <th>Domicilio</th>
                  <th>Asignar Médico</th>
                </tr>
              </thead>
              <tbody>
                {solicitudes.map((sol) => (
                  <FilaSolicitud
                    key={sol.id}
                    solicitud={sol}
                    medicos={medicos.filter((m) => m.refepsValidada === 1 && m.firmaEnrolado === 1 && m.activo === 1)}
                    onAsignado={cargarDatos}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

function FilaSolicitud({
  solicitud,
  medicos,
  onAsignado,
}: {
  solicitud: Solicitud;
  medicos: Medico[];
  onAsignado: () => Promise<void>;
}) {
  const [medicoSeleccionado, setMedicoSeleccionado] = useState('');
  const [asignando, setAsignando] = useState(false);
  const toast = useToast();

  async function asignar() {
    if (!medicoSeleccionado) {
      alert('Por favor selecciona un médico');
      return;
    }
    setAsignando(true);
    try {
      await api.post(`/api/operador/solicitudes/${solicitud.id}/asignar`, {
        medicoId: medicoSeleccionado,
      });
      const med = medicos.find((m) => m.id === medicoSeleccionado);
      toast.mostrar(`Control médico asignado a Dr/a. ${med?.nombre || ''}`);
      await onAsignado();
    } catch (err) {
      alert(err instanceof ErrorApi ? err.message : 'Error al asignar el médico');
    } finally {
      setAsignando(false);
    }
  }

  return (
    <tr>
      <td>
        <strong>{solicitud.trabajador.nombre}</strong>
        <div className="mini">DNI {solicitud.trabajador.dni} {solicitud.trabajador.legajo ? `· Legajo ${solicitud.trabajador.legajo}` : ''}</div>
      </td>
      <td>{solicitud.empresaNombre}</td>
      <td>{solicitud.fechaInicio}</td>
      <td>{solicitud.tipoControlNombre}</td>
      <td>{solicitud.domicilio}</td>
      <td>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select
            value={medicoSeleccionado}
            onChange={(e) => setMedicoSeleccionado(e.target.value)}
            disabled={asignando}
            style={{ width: 'auto', minWidth: 160 }}
          >
            <option value="">-- Seleccionar Médico --</option>
            {medicos.map((m) => (
              <option key={m.id} value={m.id}>
                {m.nombre}
              </option>
            ))}
          </select>
          <button
            className="primario mini"
            disabled={!medicoSeleccionado || asignando}
            onClick={asignar}
            style={{ width: 'auto' }}
          >
            {asignando ? 'Asignando...' : 'Asignar'}
          </button>
        </div>
      </td>
    </tr>
  );
}
